// Trip quiz recommendation engine
// Pure functions — no side effects. Takes user answers + catalog, returns ranked items.

export type Traveler = "solo" | "couple" | "family" | "friends";
export type Vibe = "relax" | "adventure" | "party" | "culture" | "family_fun";
export type Stay = "boutique_hotels" | "villas" | "apartments" | "any";
export type Budget = "low" | "mid" | "premium";

export interface QuizAnswers {
  traveler: Traveler | null;
  days: number | null; // 1-14
  vibes: Vibe[];
  stay: Stay | null;
  budget: Budget | null;
}

export interface CatalogItem {
  id: string;
  source: "service" | "boat" | "accommodation";
  title: string;
  image: string | null;
  slug: string;
  category: string; // services.category | boat | accommodation
  price: number; // numeric price for sorting (0 if unknown)
  priceLabel: string | null;
  capacity: number | null;
  tags: string[];
  href: string; // route to detail page
}

export interface ScoredItem extends CatalogItem {
  score: number;
  reason: string;
}

const vibeToCategories: Record<Vibe, string[]> = {
  adventure: ["boat_rental", "boat", "cruise", "activity"],
  party: ["boat_party"],
  relax: ["accommodation", "cruise"],
  culture: ["activity"],
  family_fun: ["cruise", "activity", "accommodation"],
};

const stayToType: Record<Stay, string[]> = {
  boutique_hotels: ["boutique_hotels", "hotels"],
  villas: ["villas"],
  apartments: ["apartments"],
  any: [],
};

function categoryReason(cat: string, vibes: Vibe[]): string {
  if (vibes.includes("party") && cat === "boat_party") return "Matches your party vibe";
  if (vibes.includes("adventure") && (cat === "boat_rental" || cat === "boat")) return "Perfect for your adventure mood";
  if (vibes.includes("relax") && cat === "accommodation") return "A relaxing stay tailored to you";
  if (vibes.includes("culture") && cat === "activity") return "A cultural experience you'll love";
  if (cat === "cruise") return "A must-do in Skiathos";
  if (cat === "accommodation") return "Great place to call home";
  if (cat === "activity") return "Unforgettable local experience";
  return "Hand-picked for your trip";
}

export function scoreItem(item: CatalogItem, answers: QuizAnswers): number {
  let score = 0;
  const vibes = answers.vibes;

  // Vibe match
  for (const v of vibes) {
    if (vibeToCategories[v]?.includes(item.category)) score += 10;
  }

  // Stay preference
  if (item.source === "accommodation" && answers.stay && answers.stay !== "any") {
    if (stayToType[answers.stay].includes(item.category)) score += 8;
    else score -= 4;
  }

  // Traveler / capacity matching
  if (answers.traveler === "family" || answers.traveler === "friends") {
    if (item.capacity && item.capacity >= 6) score += 3;
  }
  if (answers.traveler === "couple") {
    if (item.capacity && item.capacity <= 4 && item.capacity > 0) score += 2;
  }
  if (answers.traveler === "solo" && item.source === "service") {
    score += 1;
  }

  // Budget alignment (rough)
  if (answers.budget && item.price > 0) {
    if (answers.budget === "low" && item.price <= 50) score += 3;
    else if (answers.budget === "mid" && item.price > 50 && item.price <= 150) score += 3;
    else if (answers.budget === "premium" && item.price > 150) score += 4;
    else if (answers.budget === "low" && item.price > 200) score -= 5;
  }

  // Slight diversity boost for items with images
  if (item.image) score += 1;

  return score;
}

/**
 * Returns top recommendations with diversity across categories.
 * Tries to ensure mix: at least 1 accommodation, 1 cruise/boat, 1 activity if available.
 */
export function recommend(catalog: CatalogItem[], answers: QuizAnswers, limit = 6): ScoredItem[] {
  const scored: ScoredItem[] = catalog
    .map((it) => ({
      ...it,
      score: scoreItem(it, answers),
      reason: categoryReason(it.category, answers.vibes),
    }))
    .sort((a, b) => b.score - a.score);

  // Enforce diversity: pick top from each group, then fill
  const buckets: Record<string, ScoredItem[]> = {
    stay: scored.filter((s) => s.source === "accommodation"),
    boat: scored.filter((s) => ["boat", "boat_rental", "cruise"].includes(s.category)),
    activity: scored.filter((s) => s.category === "activity"),
    party: scored.filter((s) => s.category === "boat_party"),
    other: scored.filter((s) => s.category === "transfer"),
  };

  const picks: ScoredItem[] = [];
  const seen = new Set<string>();
  const wantStay = answers.stay !== null;
  const wantParty = answers.vibes.includes("party");

  const pushUnique = (it?: ScoredItem) => {
    if (it && !seen.has(it.id)) {
      seen.add(it.id);
      picks.push(it);
    }
  };

  // Guarantee at least one of each relevant bucket
  if (wantStay) pushUnique(buckets.stay[0]);
  pushUnique(buckets.boat[0]);
  if (answers.vibes.includes("culture") || answers.vibes.includes("family_fun") || answers.vibes.length === 0) {
    pushUnique(buckets.activity[0]);
  }
  if (wantParty) pushUnique(buckets.party[0]);

  // Fill remaining slots with highest scoring overall
  for (const item of scored) {
    if (picks.length >= limit) break;
    pushUnique(item);
  }

  return picks.slice(0, limit);
}
